import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileText, Thermometer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { writeAuditLog } from '@/lib/audit';
import { parseHwinfoLog, type HwinfoMetrics } from '@/lib/hwinfo';
import {
  fetchStressTests, saveStressTest, stressTestFileUrl, formatDuration, type StressTest,
} from '@/lib/api/stressTests';

interface Props {
  assemblyOrderId: string;
  orderNumber?: string;
  unitNumber: number;
  technicianId?: string | null;
  createdBy?: string | null;
}

/**
 * Per-unit HWiNFO stress-log upload, used in the technician sign-off flow.
 *
 * Self-contained on purpose: it saves through its own button rather than
 * joining the sign-off submit path, so a failed R2 upload can never block a
 * technician from signing an order off. A stress result is evidence, not a
 * gate — the checklist item is what says the test was run.
 */
export function StressTestUpload({
  assemblyOrderId, orderNumber, unitNumber, technicianId, createdBy,
}: Props) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<HwinfoMetrics | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: existing = [] } = useQuery({
    queryKey: ['stress-tests', assemblyOrderId],
    queryFn: () => fetchStressTests(assemblyOrderId),
    enabled: !!assemblyOrderId,
  });
  const saved = existing.find(t => t.unit_number === unitNumber) ?? null;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { metrics, error } = await parseHwinfoLog(f);
      if (error) {
        toast.error(error);
        setFile(null); setParsed(null);
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
      setFile(f);
      setParsed(metrics);
      if (metrics.unmatched.length > 0) {
        // Not a failure: an AMD board logs sensors an Intel one does not. Say
        // which ones are missing rather than showing silent blanks.
        toast.warning(`Log read, but ${metrics.unmatched.length} sensor${metrics.unmatched.length > 1 ? 's were' : ' was'} not in this file`);
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!parsed) return;
    setBusy(true);
    try {
      await saveStressTest({
        assemblyOrderId, unitNumber, technicianId, createdBy, file, metrics: parsed,
      });
      writeAuditLog({
        action: 'assembly_order_stress_test_recorded',
        entity_type: 'assembly_order',
        entity_id: assemblyOrderId,
        entity_label: orderNumber ?? assemblyOrderId,
        new_value: {
          unit: unitNumber,
          cpu_temp_max: parsed.cpuTempMax,
          gpu_temp_max: parsed.gpuTempMax,
          throttled: parsed.thermalThrottled,
        },
        notes: `HWiNFO stress log recorded for unit ${unitNumber}.`,
      });
      toast.success(`Unit ${unitNumber} stress result saved`);
      setFile(null); setParsed(null);
      if (fileRef.current) fileRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['stress-tests', assemblyOrderId] });
    } catch (err) {
      toast.error(`Could not save: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  const shown = parsed ?? (saved ? fromRow(saved) : null);
  const savedUrl = saved ? stressTestFileUrl(saved) : null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
        <Thermometer className="h-3.5 w-3.5" />
        Stress Test (HWiNFO log){unitNumber ? ` (Unit ${unitNumber})` : ''}
        {saved && !parsed && (
          <span className="ml-auto inline-flex items-center gap-1 text-green-600 normal-case tracking-normal font-normal">
            <CheckCircle2 className="h-3 w-3" />saved
          </span>
        )}
      </p>

      {shown && (
        <div className="rounded-md border p-2.5 space-y-2">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            <Stat label="CPU max" value={temp(shown.cpuTempMax)} />
            <Stat label="GPU max" value={temp(shown.gpuTempMax)} />
            <Stat label="Hotspot" value={temp(shown.gpuHotspotMax)} />
            <Stat label="GPU power" value={shown.gpuPowerMax === null ? '—' : `${shown.gpuPowerMax} W`} />
            <Stat label="CPU clock" value={clockRange(shown.cpuClockMin, shown.cpuClockMax)} />
            <Stat label="GPU clock" value={clockRange(shown.gpuClockMin, shown.gpuClockMax)} />
            <Stat label="Fan max" value={shown.fanRpmMax === null ? '—' : `${shown.fanRpmMax} rpm`} />
            <Stat label="Duration" value={formatDuration(shown.durationSeconds)} />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {shown.thermalThrottled === true && (
              <span className="inline-flex items-center gap-1 text-orange-600">
                <AlertTriangle className="h-3 w-3" />throttled during the run
              </span>
            )}
            {shown.thermalThrottled === false && (
              <span className="text-muted-foreground">no throttling reported</span>
            )}
            {shown.thermalThrottled === null && (
              <span className="text-muted-foreground">no throttling sensor in this log</span>
            )}
            <span className="text-muted-foreground ml-auto tabular-nums">
              {shown.sampleCount} samples
            </span>
          </div>
          {shown.unmatched.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Not logged by this machine: {shown.unmatched.join(', ')}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.txt"
          className="hidden"
          onChange={handleFile}
        />
        <Button
          type="button" size="sm" variant="outline" disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
          {saved && !parsed ? 'Replace log' : 'Choose HWiNFO log'}
        </Button>

        {parsed && (
          <Button type="button" size="sm" disabled={busy} onClick={save}>
            Save result
          </Button>
        )}

        {file && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1 min-w-0">
            <FileText className="h-3 w-3 shrink-0" />
            <span className="truncate">{file.name}</span>
          </span>
        )}
        {!file && saved?.file_name && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1 min-w-0">
            <FileText className="h-3 w-3 shrink-0" />
            {savedUrl
              ? <a href={savedUrl} target="_blank" rel="noopener noreferrer"
                  className="truncate text-primary hover:underline">{saved.file_name}</a>
              : <span className="truncate">{saved.file_name}</span>}
          </span>
        )}
      </div>
    </div>
  );
}

/** Render a saved row through the same shape the parser produces. */
function fromRow(t: StressTest): HwinfoMetrics {
  const m = (t.metrics ?? {}) as {
    unmatched?: string[];
    matchedSensors?: Record<string, string>;
    otherThrottleReasons?: string[];
    dateAmbiguous?: boolean;
  };
  return {
    sampleCount: t.sample_count ?? 0,
    durationSeconds: t.duration_seconds,
    startedAt: t.started_at,
    dateAmbiguous: m.dateAmbiguous ?? false,
    cpuTempMax: t.cpu_temp_max, cpuTempAvg: t.cpu_temp_avg,
    gpuTempMax: t.gpu_temp_max, gpuTempAvg: t.gpu_temp_avg,
    gpuHotspotMax: t.gpu_hotspot_max,
    cpuClockMin: t.cpu_clock_min, cpuClockMax: t.cpu_clock_max,
    gpuClockMin: t.gpu_clock_min, gpuClockMax: t.gpu_clock_max,
    gpuPowerMax: t.gpu_power_max, fanRpmMax: t.fan_rpm_max,
    thermalThrottled: t.thermal_throttled,
    otherThrottleReasons: m.otherThrottleReasons ?? [],
    matchedSensors: m.matchedSensors ?? {},
    unmatched: m.unmatched ?? [],
  };
}

const temp = (v: number | null): string => (v === null ? '—' : `${v}°C`);
const clockRange = (lo: number | null, hi: number | null): string =>
  hi === null ? '—' : lo === null || lo === hi ? `${hi}` : `${lo}–${hi}`;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={cn('text-sm font-medium tabular-nums', value === '—' && 'text-muted-foreground')}>{value}</p>
    </div>
  );
}
